import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Request {
  requestId: number;
  jobId: string;
  requestType: string;
  reason: string;
  requestedOn: string;
  requestedBy: string;
  jobName: string | null;
  totalBoxes: number | null;
  poNumber: string | null;
  clientName: string | null;
  producedQty: number;
  dispatchedQty: number;
  scrappedQty: number;
}

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ApprovalRequestsPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [remarks, setRemarks] = useState<Record<number, string>>({});

  const list = useQuery({
    queryKey: ['approval-requests', 'Reissue'],
    queryFn: () => {
      const sp = new URLSearchParams({ type: 'Reissue' });
      return api<{ rows: Request[] }>(`/api/flow/requests?${sp.toString()}`);
    },
  });

  const act = useMutation({
    mutationFn: (v: { id: number; action: string; remark?: string }) =>
      api(`/api/flow/requests/${v.id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: v.action, remark: v.remark }),
      }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['approval-requests'] });
      qc.invalidateQueries({ queryKey: ['flow-queue'] });
    },
    onError: (e) => setError((e as Error).message),
    onSettled: () => setBusy(null),
  });

  const rows = list.data?.rows ?? [];

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-brand">Job Card Requests</h1>
        <p className="text-sm text-slate-500">Pending reissue requests from CRM.</p>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="card overflow-hidden">
        <div className="card-header flex justify-between">
          <span>Pending Requests</span>
          <span className="text-xs font-normal">{rows.length} request{rows.length === 1 ? '' : 's'}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2">Job ID</th>
                <th className="px-3 py-2">PO</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">PO Qty</th>
                <th className="px-3 py-2 text-right">Produced</th>
                <th className="px-3 py-2 text-right">Dispatched</th>
                <th className="px-3 py-2 text-right">Remaining</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Requested</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={12} className="p-8 text-center text-slate-500">Loading...</td></tr>}
              {!list.isLoading && rows.length === 0 && (
                <tr><td colSpan={12} className="p-8 text-center text-slate-500">No pending requests.</td></tr>
              )}
              {rows.map((r) => {
                const remaining = r.producedQty - r.dispatchedQty - r.scrappedQty;
                return (
                  <tr key={r.requestId} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{r.jobId}</td>
                    <td className="px-3 py-2 text-xs">{r.poNumber}</td>
                    <td className="px-3 py-2 text-xs">{r.clientName}</td>
                    <td className="px-3 py-2 text-xs">{r.jobName}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
                        {r.requestType}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-right">{r.totalBoxes ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-right">{r.producedQty}</td>
                    <td className="px-3 py-2 text-xs text-right">{r.dispatchedQty}</td>
                    <td className="px-3 py-2 text-xs text-right font-semibold">{remaining}</td>
                    <td className="px-3 py-2 text-xs max-w-xs truncate" title={r.reason}>{r.reason}</td>
                    <td className="px-3 py-2 text-xs">
                      <div>{fmtDate(r.requestedOn)}</div>
                      <div className="text-slate-400">{r.requestedBy}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <input className="input py-1 text-xs w-40" placeholder="Remark (optional)"
                               value={remarks[r.requestId] ?? ''}
                               onChange={(e) => setRemarks((p) => ({ ...p, [r.requestId]: e.target.value }))} />
                        <div className="flex gap-1">
                          <button className="btn-primary text-xs py-1"
                                  disabled={busy === r.requestId}
                                  onClick={() => { setBusy(r.requestId); act.mutate({ id: r.requestId, action: 'Approved', remark: remarks[r.requestId] }); }}>
                            Approve
                          </button>
                          <button className="btn-ghost text-xs py-1 text-red-600"
                                  disabled={busy === r.requestId}
                                  onClick={() => { setBusy(r.requestId); act.mutate({ id: r.requestId, action: 'Rejected', remark: remarks[r.requestId] }); }}>
                            Reject
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
